import React, { useMemo, useState } from 'react';
import {
  ArrowRight,
  BadgeCheck,
  BookOpen,
  CheckCircle2,
  ExternalLink,
  Film,
  Globe2,
  Lightbulb,
  MessageSquare,
  Newspaper,
  Search,
  ShieldCheck,
  Upload,
  Wheat,
} from 'lucide-react';
import { mediaCategoryOptions, mediaSections } from '../../data/mediaLibrary';

const STORAGE_KEY = 'tyk.media.submissions.v1';

const blankSubmission = {
  url: '',
  title: '',
  whyItMatters: '',
  category: 'testimonies',
  tags: '',
  claimsToCheck: '',
  urgent: false,
};

function getSavedSubmissions() {
  if (typeof window === 'undefined') {
    return [];
  }

  try {
    const parsed = JSON.parse(window.localStorage.getItem(STORAGE_KEY) || '[]');
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function saveSubmissions(submissions) {
  if (typeof window === 'undefined') {
    return;
  }

  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(submissions));
}

function normalizeTags(tags) {
  return tags
    .split(',')
    .map((tag) => tag.trim())
    .filter(Boolean);
}

function getDomain(url) {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return 'Link pending';
  }
}

function getAllItems() {
  return mediaSections.flatMap((section) =>
    section.items.map((item) => ({
      ...item,
      categoryId: section.id,
      categoryTitle: section.title,
      accent: section.accent,
    })),
  );
}

export default function MediaPage() {
  const [activeSection, setActiveSection] = useState(mediaSections[0].id);
  const [query, setQuery] = useState('');
  const [submission, setSubmission] = useState(blankSubmission);
  const [submissions, setSubmissions] = useState(() => getSavedSubmissions());
  const active = mediaSections.find((section) => section.id === activeSection) || mediaSections[0];

  const filteredItems = useMemo(() => {
    const search = query.trim().toLowerCase();
    const sectionItems = active.items.map((item) => ({
      ...item,
      categoryId: active.id,
      categoryTitle: active.title,
      accent: active.accent,
    }));

    if (!search) {
      return sectionItems;
    }

    return getAllItems().filter((item) =>
      [item.title, item.source, item.type, item.summary, item.categoryTitle, ...(item.tags || [])]
        .join(' ')
        .toLowerCase()
        .includes(search),
    );
  }, [active, query]);

  const handleSubmissionChange = (event) => {
    const { name, value, type, checked } = event.target;
    setSubmission((current) => ({
      ...current,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSubmit = (event) => {
    event.preventDefault();

    const nextSubmission = {
      ...submission,
      id: `media-${Date.now()}`,
      submittedAt: new Date().toISOString(),
      source: getDomain(submission.url),
      tags: normalizeTags(submission.tags),
      status: 'Needs discernment',
    };
    const next = [nextSubmission, ...submissions];
    setSubmissions(next);
    saveSubmissions(next);
    setSubmission(blankSubmission);
  };

  return (
    <main className="media-page">
      <section className="media-hero" aria-labelledby="media-hero-heading">
        <div>
          <span className="media-kicker">
            <Wheat size={18} aria-hidden="true" />
            Kingdom media desk
          </span>
          <h1 id="media-hero-heading">Wake up. Test everything. Move toward action.</h1>
          <p>
            This is a place for testimonies, documentary work, practical learning, market awareness, and sober news
            discovery. The goal is not doom-scrolling. The goal is discernment that turns into prayer, wisdom, and
            faithful work.
          </p>
        </div>
        <aside className="media-discernment-card" aria-label="Discernment principles">
          <ShieldCheck size={26} aria-hidden="true" />
          <h2>Discernment over outrage</h2>
          <p>
            Every media item should be treated as something to test: what is true, what is missing, who is helped, who is
            harmed, and what faithful response may be required.
          </p>
        </aside>
      </section>

      <section className="media-submit-panel" aria-labelledby="media-submit-heading">
        <div className="media-section-heading">
          <span className="media-kicker">
            <Upload size={18} aria-hidden="true" />
            Submit media
          </span>
          <h2 id="media-submit-heading">Drop a link, then tell the Body why it matters.</h2>
          <p>
            Start with a link. Add enough context that other members can ask better questions, check claims, and connect
            the piece to prayer, learning, or action.
          </p>
        </div>
        <form className="media-submit-form" onSubmit={handleSubmit}>
          <label>
            Link
            <input
              name="url"
              type="url"
              value={submission.url}
              onChange={handleSubmissionChange}
              placeholder="https://..."
              required
            />
          </label>
          <label>
            Title
            <input
              name="title"
              type="text"
              value={submission.title}
              onChange={handleSubmissionChange}
              placeholder="What should people call this?"
              required
            />
          </label>
          <label>
            Category
            <select name="category" value={submission.category} onChange={handleSubmissionChange}>
              {mediaCategoryOptions.map(({ id, title }) => (
                <option key={id} value={id}>{title}</option>
              ))}
            </select>
          </label>
          <label>
            Mission tags
            <input
              name="tags"
              type="text"
              value={submission.tags}
              onChange={handleSubmissionChange}
              placeholder="trafficking, debt freedom, evangelism"
            />
          </label>
          <label className="media-wide-field">
            Why this matters
            <textarea
              name="whyItMatters"
              value={submission.whyItMatters}
              onChange={handleSubmissionChange}
              placeholder="What should believers notice, pray over, or investigate?"
              required
            />
          </label>
          <label className="media-wide-field">
            Claims worth checking
            <textarea
              name="claimsToCheck"
              value={submission.claimsToCheck}
              onChange={handleSubmissionChange}
              placeholder="List claims, statistics, source questions, or possible missing context."
            />
          </label>
          <label className="media-checkbox">
            <input
              name="urgent"
              type="checkbox"
              checked={submission.urgent}
              onChange={handleSubmissionChange}
            />
            Mark as urgent
          </label>
          <button type="submit" className="media-primary-action">
            Add to discernment queue <ArrowRight size={18} aria-hidden="true" />
          </button>
        </form>
      </section>

      <section className="media-library" aria-labelledby="media-library-heading">
        <div className="media-library-topline">
          <div className="media-section-heading">
            <span className="media-kicker">
              <BookOpen size={18} aria-hidden="true" />
              Starter library
            </span>
            <h2 id="media-library-heading">Sixty pieces to start the conversation</h2>
            <p>
              Ten hand-picked starting points in each section. They are not endorsements of every word; they are
              invitations to learn, question, compare, and respond wisely.
            </p>
          </div>
          <label className="media-search">
            <Search size={18} aria-hidden="true" />
            <span className="sr-only">Search media</span>
            <input
              type="search"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search source, topic, or tag"
            />
          </label>
        </div>

        <div className="media-tabs" role="tablist" aria-label="Media sections">
          {mediaSections.map((section) => (
            <button
              key={section.id}
              type="button"
              className={section.id === activeSection ? 'is-active' : ''}
              onClick={() => setActiveSection(section.id)}
              aria-selected={section.id === activeSection}
            >
              <span>{section.shortTitle}</span>
              <small>{section.accent}</small>
            </button>
          ))}
        </div>

        <div className="media-active-section">
          <div>
            <h3>{query ? 'Search results' : active.title}</h3>
            <p>{query ? `${filteredItems.length} matching media items` : active.description}</p>
          </div>
          <span>{filteredItems.length} items</span>
        </div>

        <div className="media-card-grid">
          {filteredItems.map((item) => (
            <article key={`${item.categoryId}-${item.title}`} className="media-card">
              <div className="media-card-icon" aria-hidden="true">
                {item.type.toLowerCase().includes('video') ? <Film size={24} /> : <Newspaper size={24} />}
              </div>
              <div className="media-card-body">
                <div className="media-card-meta">
                  <span>{item.categoryTitle}</span>
                  <span>{item.type}</span>
                </div>
                <h3>{item.title}</h3>
                <p>{item.summary}</p>
                <div className="media-tags">
                  {item.tags.map((tag) => (
                    <span key={tag}>{tag}</span>
                  ))}
                </div>
              </div>
              <div className="media-card-footer">
                <span>{item.source}</span>
                <a href={item.url} target="_blank" rel="noopener noreferrer">
                  Open <ExternalLink size={15} aria-hidden="true" />
                </a>
              </div>
            </article>
          ))}
        </div>
      </section>

      <section className="media-review-panel" aria-labelledby="media-review-heading">
        <div>
          <span className="media-kicker">
            <MessageSquare size={18} aria-hidden="true" />
            Discernment review
          </span>
          <h2 id="media-review-heading">How members should beat up a media item</h2>
          <p>
            Good review is not cynical. It is loving enough to tell the truth and careful enough not to pass along lies.
          </p>
        </div>
        <div className="media-review-grid">
          {[
            ['Useful', 'Does this help someone understand, pray, build, serve, or act?'],
            ['Well sourced', 'Can we find primary evidence, witnesses, documents, data, or multiple credible reports?'],
            ['Needs context', 'What is missing, exaggerated, emotionally loaded, or framed for a tribe?'],
            ['Actionable', 'Can this connect to a Kingdom Circle, idea, globe signal, service, job, or campaign?'],
          ].map(([title, body]) => (
            <article key={title}>
              <CheckCircle2 size={20} aria-hidden="true" />
              <h3>{title}</h3>
              <p>{body}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="media-submission-queue" aria-labelledby="media-queue-heading">
        <div className="media-section-heading">
          <span className="media-kicker">
            <Globe2 size={18} aria-hidden="true" />
            Member queue
          </span>
          <h2 id="media-queue-heading">Pending local submissions</h2>
          <p>
            These are saved on this device for now. The production version should persist them through a shared media
            submissions API with moderation, reviews, and claim-checking history.
          </p>
        </div>
        {submissions.length === 0 ? (
          <div className="media-empty-state">
            <BadgeCheck size={24} aria-hidden="true" />
            <p>No pending submissions yet. Drop a link above to see the review flow.</p>
          </div>
        ) : (
          <div className="media-pending-list">
            {submissions.map((item) => (
              <article key={item.id}>
                <div>
                  <span>{item.status}</span>
                  <h3>{item.title}</h3>
                  <p>{item.whyItMatters}</p>
                  {item.claimsToCheck && <small>Claims to check: {item.claimsToCheck}</small>}
                </div>
                <a href={item.url} target="_blank" rel="noopener noreferrer">
                  {item.source} <ExternalLink size={15} aria-hidden="true" />
                </a>
              </article>
            ))}
          </div>
        )}
      </section>
    </main>
  );
}
